import React, { useEffect, useState } from 'react';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Tooltip, Button, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserRoleApiData } from '@app/models/user_roles/UserRolesResponse';
import { UserRolesRequest, Permission, PermissionOption } from '@app/models/user_roles/UserRolesRequest';
import { storeUserRole, showUserRole, editUserRole, updateUserRole, createUserRole } from '@app/api/userRoles.api';
import { notificationController } from '@app/controllers/notificationController';
import type { ColumnType } from 'antd/es/table';
import StatusBar from '@app/components/statusBar/statusBar';
import { getButtonsByStatus } from '@app/components/statusBar/actionButtons';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface RoleFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  showEditIcon?: boolean;
  pageTitle: string;
}

const RolePermissionsForm: React.FC<RoleFormProps> = ({ editable = false, id, pageTitle, type }) => {
  const [form] = BaseButtonsForm.useForm();
  const [isLoading, setLoading] = useState(false);
  const [permissionOptions, setPermissionOptions] = useState<PermissionOption[]>([]);

  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const roleId = id ? parseInt(id) : null;

  const setApiData = (res: UserRoleApiData, allPermissions: any) => {
    form.setFieldsValue({
      role_name: res?.role?.role_name,
      description: res?.role?.description,
      permission_id: res?.role?.permissions,
    });

    const selected = res?.role_permissions.map((p: any) => ({
      id: p.id,
      permission_id: p.permission_id,
      description: p.description,
    }));

    setSelectedPermissions(selected);

    const selectedIds = new Set(selected.map((p) => p.id));
    const availableOptions = allPermissions.filter((p: { value: any }) => !selectedIds.has(p.value));
    setPermissionOptions(availableOptions);
  };

  useEffect(() => {
    setLoading(true);
    if (roleId) {
      if (type === 'view') {
        showUserRole(roleId)
          .then((res: any) => {
            const allPermissions =
              res.permissions?.map((p: any) => ({
                label: p.description,
                value: p.id,
                permission_id: p.permission_id,
              })) || [];
            setApiData(res, allPermissions);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to view role', description: err.message });
            console.error(err);
          });
      } else if (type === 'edit') {
        editUserRole(roleId)
          .then((res: any) => {
            const allPermissions = res.permissions.map((p: any) => ({
              label: p.description,
              value: p.id,
              permission_id: p.permission_id,
            }));
            setApiData(res, allPermissions);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit role', description: err.message });
            console.error(err);
          });
      }
    } else if (type === 'create') {
      createUserRole()
        .then((res: any) => {
          const allPermissions = res.permissions.map((p: any) => ({
            label: p.description,
            value: p.id,
          }));
          setPermissionOptions(allPermissions);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch permissions', description: err.message });
          console.error(err);
        });
    }
  }, []);

  const handlePermissionSelect = (value: unknown) => {
    const selectedValues = value as number[];

    const newSelectedPermissions = permissionOptions
      .filter((p) => selectedValues.includes(p.value))
      .map((p) => ({
        id: p.value,
        permission_id: p.permission_id,
        description: p.label,
      }));

    setSelectedPermissions([...selectedPermissions, ...newSelectedPermissions]);

    const remainingOptions = permissionOptions.filter((p) => !selectedValues.includes(p.value));
    setPermissionOptions(remainingOptions);

    form.setFieldValue('permissions', []);
  };

  const handleRemovePermission = (id: number) => {
    const removedPermission = selectedPermissions.find((p) => p.id === id);

    if (removedPermission) {
      const newOption: PermissionOption = {
        value: removedPermission.id,
        permission_id: removedPermission.permission_id,
        label: removedPermission.description,
      };

      setPermissionOptions((prevOptions) => [...prevOptions, newOption].sort((a, b) => a.label.localeCompare(b.label)));
    }

    setSelectedPermissions(selectedPermissions.filter((p) => p.id !== id));
  };

  const columns: ColumnType<any>[] = [
    {
      title: 'Permission ID',
      dataIndex: 'permission_id',
      width: '30%',
      render: (text, record) => record.description,
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    ...(editable
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: any) => <Button onClick={() => handleRemovePermission(record.id)}>Remove</Button>,
          },
        ]
      : []),
  ];

  const handleSubmit = (values: UserRolesRequest) => {
    if (editable) {
      const submissionData = {
        ...values,
        permissions: selectedPermissions.map((p) => p.id),
      };

      if (type === 'create') {
        storeUserRole(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-role/${res?.data?.id}`);
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && roleId) {
        updateUserRole(roleId, submissionData)
          .then(() => {
            setLoading(false);
            navigate(`/view-role/${id}`);
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
  };

  return (
    <>
      <BaseCol span={24}>
        <StatusBar
          id={String(roleId)}
          status={status}
          wfActions={[]}
          currentType={'create'}
          workflowType={''}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onViewInvoicesClick={() => {}}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          setStatus={() => {}}
        />
      </BaseCol>
      <BaseCard
        id="rolePermissionsForm"
        title={pageTitle}
        padding="1.25rem"
        style={{ margin: '20px', marginTop: '0px', width: '98%' }}
        extra={
          type === 'view' && (
            <Tooltip title="Edit">
              <Button type="ghost" onClick={() => navigate(`/edit-role/${id}`)}>
                Edit
              </Button>
            </Tooltip>
          )
        }
      >
        <BaseButtonsForm form={form} name="rolePermissionsForm" isFieldsChanged={false}>
          <BaseRow gutter={[16, 16]}>
            <BaseCol xs={24} md={8} lg={12}>
              <BaseButtonsForm.Item
                name="role_name"
                label="Role Name"
                rules={[{ required: true, message: 'Role Name is required' }]}
              >
                <BaseInput placeholder="Enter Role Name" disabled={!editable} />
              </BaseButtonsForm.Item>
            </BaseCol>
          </BaseRow>

          <BaseRow gutter={[16, 16]}>
            <BaseCol xs={24} md={8} lg={12}>
              <BaseButtonsForm.Item name="description" label="Description">
                <BaseInput.TextArea rows={4} placeholder="Enter Role Description" disabled={!editable} />
              </BaseButtonsForm.Item>
            </BaseCol>
          </BaseRow>

          {type !== 'view' && (
            <BaseRow gutter={[16, 16]}>
              <BaseCol xs={24} md={24} lg={24}>
                <BaseButtonsForm.Item label="Permissions" name="permissions">
                  <BaseSelect
                    placeholder="Select Permissions"
                    mode="multiple"
                    options={permissionOptions}
                    onChange={handlePermissionSelect}
                    disabled={!editable}
                    optionLabelProp="permission_id"
                  />
                </BaseButtonsForm.Item>
              </BaseCol>
            </BaseRow>
          )}

          <BaseRow gutter={[16, 16]}>
            <BaseCol span={24}>
              <BaseTable
                size="small"
                bordered
                columns={columns}
                dataSource={selectedPermissions}
                rowKey="id"
                scroll={{ x: true }}
              />
            </BaseCol>
          </BaseRow>
          <br />
        </BaseButtonsForm>
      </BaseCard>
    </>
  );
};

export default RolePermissionsForm;
